import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useNuxtApp, useRoute } from 'nuxt/app';
import { isOver18 as isOver18Util } from '~~/common/utils';
import type { AwNewMediaQueries } from '~~/common/utils/userInterface';
import { initialMediaQueries, transformToMediaQuery } from '~~/common/utils/userInterface';
import type { ValidatorMutated } from '~~/common/config/Validator';
import type { DeepPartial, Schema } from '~~/common/types/api';

type ImageModel = Schema<'ImageModel'>;

type BackgroundState = {
  backgroundRepeat: string | null;
  backgroundColor: {
    hex: string;
    alpha: number;
  } | null,
  backgroundMedia: {
    largeBackgroundImage: ImageModel | null;
    smallBackgroundImage: ImageModel | null;
  } | null;
};

// Callers pass CMS content (a DeepPartial of ContentVersionModel), so accept
// the loose shape and normalize it to BackgroundState in setBackground.
type BackgroundInput = {
  backgroundRepeat?: string | null;
  backgroundColor?: DeepPartial<Schema<'ColorModel'>> | null;
  media?: DeepPartial<Schema<'ContentMediaModel'>> | null;
};

function normalizeBackgroundImage (image: DeepPartial<ImageModel> | null | undefined): ImageModel | null {
  if (!image || typeof image.url !== 'string') {
    return null;
  }
  return { url: image.url, alt: image.alt ?? null };
}

export const useUserInterfaceStore = defineStore('userInterface', () => {
  const routeHistoryPath = ref<string | null>(null);
  const loading = ref<string[]>([]);
  const backdropChildren = ref<Record<string, boolean>>({});
  const openSidebarSide = ref<'right' | null>(null);
  const mediaQueries = ref(initialMediaQueries);

  function useMediaQueries () {
    const isMounted = ref(false);
    onMounted(() => {
      isMounted.value = true;
    });
    return computed(() => {
      if (isMounted.value) {
        return mediaQueries.value;
      } else {
        return initialMediaQueries;
      }
    });
  }

  const timeZoneOffsetInHours = ref(0);
  const validators = ref(Array<ValidatorMutated>());
  const background = ref<BackgroundState>({
    backgroundRepeat: null,
    backgroundColor: null,
    backgroundMedia: null,
  });
  const isExtraBodyClassNeeded = ref(false);
  const profilePaging = ref({
    page: 1,
    itemsPerPage: 3,
  });
  const profilePagingTotalRows = ref(0);
  const showPaging = ref(true);

  function setRouteHistoryPathIfChanged ({
    fullPath,
  }: {
    fullPath: string;
  }) {
    const newRouteHistoryPath = fullPath.split('#')[0] || '';
    const isPathSame = routeHistoryPath.value === newRouteHistoryPath;
    if (!isPathSame) {
      routeHistoryPath.value = newRouteHistoryPath;
    }
    return { isSame: isPathSame };
  }

  function startLoading (data: { id: string }) {
    const id = data?.id || null;
    if (id && !loading.value.includes(id)) {
      loading.value.push(id);
    }
  }

  function endLoading (data: { id: string }) {
    const id = data?.id || null;
    if (id) {
      const index = loading.value.indexOf(id);
      if (index > -1) {
        loading.value.splice(index, 1);
      }
    }
  }

  function setBackdropChildren ({
    uuid,
    value,
  }: {
    uuid: string;
    value: boolean;
  }) {
    if (import.meta.server) {
      return;
    }
    if (typeof uuid !== 'string') {
      return;
    }
    if (value) {
      backdropChildren.value[uuid] = Boolean(value);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete backdropChildren.value[uuid];
    }
  }

  function setOpenSidebarSide (payload: 'right' | null) {
    openSidebarSide.value = payload;
  }

  function setMediaQueries (payload: AwNewMediaQueries) {
    mediaQueries.value = transformToMediaQuery(payload);
  }

  function setValidators (payload: ValidatorMutated[]) {
    validators.value = payload;
  }

  function resetBackground () {
    background.value = {
      backgroundRepeat: null,
      backgroundColor: null,
      backgroundMedia: null,
    };
  }

  function setBackground ({
    backgroundRepeat,
    backgroundColor,
    media,
  }: BackgroundInput) {
    background.value = {
      backgroundRepeat: backgroundRepeat ?? null,
      backgroundColor: (backgroundColor && typeof backgroundColor.hex === 'string')
        ? { hex: backgroundColor.hex, alpha: backgroundColor.alpha ?? 1 }
        : {
          hex: '#F3F0ED',
          alpha: 1,
        },
      backgroundMedia: media
        ? {
          largeBackgroundImage: normalizeBackgroundImage(media.largeBackgroundImage),
          smallBackgroundImage: normalizeBackgroundImage(media.smallBackgroundImage),
        }
        : null,
    };
  }

  function setExtraBodyClassNeeded (payload: boolean) {
    isExtraBodyClassNeeded.value = payload;
  }

  function setProfilePaging ({
    page,
    itemsPerPage,
  }: {
    page: number;
    itemsPerPage: number;
  }) {
    profilePaging.value = {
      page,
      itemsPerPage,
    };
  }

  function resetProfilePaging () {
    profilePaging.value = {
      page: 1,
      itemsPerPage: 3,
    };
  }

  function setProfilePagingTotalRows ({
    total,
  }: {
    total: number;
  }) {
    profilePagingTotalRows.value = total;
  }

  function setShowPaging (val: boolean) {
    showPaging.value = val;
  }

  function getValidator<TValidatorName extends ValidatorMutated['validatorName']> (vName: TValidatorName) {
    type ExtractValidator<T> = Extract<ValidatorMutated, { validatorName: T }>;
    return validators.value.find((validator): validator is ExtractValidator<TValidatorName> => {
      return validator.validatorName === vName;
    });
  }

  const isBasketPage = computed(() => {
    const route = useRoute();
    const localeCodes = useNuxtApp().$i18n.localeCodes.value.map(x => `${x}/`).join('|');
    const basketRegex = new RegExp(`^/(${localeCodes})*shop/(basket|kosar)($|\\?|#)`);
    return basketRegex.test(route.fullPath);
  });

  const isCheckoutPage = computed(() => {
    const route = useRoute();
    const localeCodes = useNuxtApp().$i18n.localeCodes.value.map(x => `${x}/`).join('|');
    const basketRegex = new RegExp(`^/(${localeCodes})*shop/checkout/`);
    return basketRegex.test(route.fullPath);
  });

  const isCheckoutSuccessPage = computed(() => {
    const route = useRoute();
    const localeCodes = useNuxtApp().$i18n.localeCodes.value.map(x => `${x}/`).join('|');
    const basketRegex = new RegExp(`^/(${localeCodes})*shop/checkout-success/`);
    return basketRegex.test(route.fullPath);
  });
  const isGiftCardSuccessPage = computed(() => {
    const route = useRoute();
    const localeCodes = useNuxtApp().$i18n.localeCodes.value.map(x => `${x}/`).join('|');
    const giftCardSuccessPageRegexp = new RegExp(`^/(${localeCodes})*gift-card-order-success/`);
    return giftCardSuccessPageRegexp.test(route.fullPath);
  });

  const isBodyLocked = computed(() => {
    return Boolean(Object.keys(backdropChildren.value).length);
  });
  const str2Phone = computed(() => (pstr: string, disableDefaultAc = false) => {
    const metaData = getValidator('phone_number')?.metaData;
    if (!metaData || (typeof pstr !== 'string')) {
      return ['', '', ''];
    }
    const metaDataKeys = Object.keys(metaData);
    const ccc = metaDataKeys.find(countryCallingCode => pstr.startsWith(countryCallingCode)) || metaDataKeys[0] || '';
    const areaCodes = metaData[ccc]?.areaCodes || [];
    const ac = areaCodes.find(areaCode => pstr.startsWith(ccc + areaCode)) || (disableDefaultAc ? '' : areaCodes[0]) || '';
    const num = pstr.slice((ccc + ac).length);
    return [ccc, ac, num];
  });
  const isOver18 = computed(() => {
    return isOver18Util;
  });
  const isValidDate = computed(() => (value: string) => {
    const dateRegex = /^[12]\d{3}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?)?$/;
    return dateRegex.test(value);
  });
  const phoneValidator = computed(() => (value: [ccc: string, ac: string, rest: string]) => {
    const validator = getValidator('phone_number');
    if (!validator || value.length !== 3) {
      return false;
    }
    const [ccc, ac, rest] = value;
    const metaForPrefix = validator.metaData[ccc];
    if (!metaForPrefix) {
      return false;
    }
    const validAreaCodes = metaForPrefix.areaCodes;
    if (!validAreaCodes) {
      return false;
    }
    const fullRegex = new RegExp(validator.validationString);
    let partialRegex;
    {
      const regexString = metaForPrefix.regex;
      if (regexString === undefined) {
        return false;
      }
      partialRegex = new RegExp(regexString);
    }
    return (
      validAreaCodes.includes(ac)
        && partialRegex.test(rest)
        && fullRegex.test(value.join(''))
    );
  });
  const ucbAddressValidator = computed(() => (value: string) => {
    const validator = getValidator('ucb_address');
    if (!validator || typeof value !== 'string') {
      return false;
    }
    return (new RegExp(validator.metaData.regex)).test(value);
  });
  const groupIdNumberValidator = computed(() => (value: string) => {
    const validator = getValidator('group_id_number');
    if (!validator || typeof value !== 'string') {
      return false;
    }
    return (new RegExp(validator.metaData.regex)).test(value);
  });
  const taxNumberValidator = computed(() => (value: string) => {
    const validator = getValidator('tax_number');
    if (!validator || typeof value !== 'string') {
      return false;
    }
    return (new RegExp(validator.validationString)).test(value);
  });
  const taxIdValidator = computed(() => () => {
    return getValidator('tax_id_number');
  });
  const taxIdValidate = computed(() => (value: string) => {
    const validator = taxIdValidator.value();
    if (!validator || typeof value !== 'string') {
      return false;
    }
    return (new RegExp(validator.validationString)).test(value);
  });
  const tajValidator = computed(() => () => {
    return getValidator('taj_number');
  });
  const tajValidate = computed(() => (value: string) => {
    const validator = tajValidator.value();
    if (!validator || typeof value !== 'string') {
      return false;
    }
    return (new RegExp(validator.validationString)).test(value);
  });
  // TODO: later get this validator from backend
  const postCodeValidator = computed(() => {
    return (
      value: string | number,
      disable = false,
    ) => {
      if (disable) {
        return true;
      }
      return /^([0-9]{4})$/.test(String(value));
    };
  });
  const taxNumberInputLength = computed(() => {
    const validator = getValidator('tax_number');
    if (!validator) {
      return false;
    }
    const {
      metaData: {
        lengths,
        separator,
      },
    } = validator;
    const lengthsNumber = ((Array.isArray(lengths) ? lengths : [])
      .filter((x: unknown) => typeof x === 'number')
    );
    return lengthsNumber.reduce((a, b) => a + b) + (lengthsNumber.length - 1) * separator.length;
  });
  const hypenateTax = computed(() => (value: string) => {
    const validator = getValidator('tax_number');
    if (!validator) {
      return false;
    }
    const {
      separator,
      lengths,
    } = validator.metaData;
    const separatorless = value.replace(new RegExp(separator, 'g'), '');
    // https://stackoverflow.com/questions/47095238/sum-of-previous-elements-from-an-array-in-javascript
    const indexes = (Array.isArray(lengths) ? lengths : []).map(((s) => {
      return (a: number) => {
        s += a;
        return s;
      };
    })(0));
    const s = indexes.map((a, idx) => separatorless.slice(indexes[idx - 1], a)).filter(e => e);
    return s.join('-');
  });
  const updateCartLoading = computed(() => {
    const cartLoadingIds = ['update-cart-item-', 'remove-cart-item-', 'remove-all-item-'] as const;
    return loading.value.some(id => cartLoadingIds.some(a => id.startsWith(a)));
  });
  const basketOperationLoading = computed(() => {
    const cartLoadingIds = ['add-to-cart-', 'update-cart-item-', 'remove-cart-item-', 'remove-all-item-', 'fetch-order-', 'add-to-coupon-'] as const;
    return loading.value.some(id => cartLoadingIds.some(a => id.startsWith(a)));
  });

  if (import.meta.client) {
    watch(isExtraBodyClassNeeded, (newValue) => {
      document.body.classList.toggle('version2', newValue);
    }, { immediate: true });
    watch(isBodyLocked, (newValue) => {
      document.body.classList.toggle('body-locked', newValue);
      newValue
        ? document.body.style.overflow = 'hidden'
        : document.body.style.removeProperty('overflow')
      ;
    }, { immediate: true });
  }

  function setTimeZoneOffsetInHours () {
    timeZoneOffsetInHours.value = getRealtimeTimeZoneOffsetInHours();
  }

  const timeZoneOffsetDiffInHours = computed(() => {
    return getRealtimeTimeZoneOffsetInHours() - timeZoneOffsetInHours.value;
  });

  return {
    timeZoneOffsetInHours,
    setTimeZoneOffsetInHours,
    timeZoneOffsetDiffInHours,
    routeHistoryPath,
    loading,
    backdropChildren,
    mediaQueries,
    useMediaQueries,
    openSidebarSide,
    validators,
    background,
    isExtraBodyClassNeeded,
    profilePaging,
    profilePagingTotalRows,
    showPaging,
    setRouteHistoryPathIfChanged,
    startLoading,
    endLoading,
    setBackdropChildren,
    setOpenSidebarSide,
    setMediaQueries,
    setValidators,
    resetBackground,
    setBackground,
    setExtraBodyClassNeeded,
    setProfilePaging,
    resetProfilePaging,
    setProfilePagingTotalRows,
    setShowPaging,
    isCheckoutPage,
    isCheckoutSuccessPage,
    isGiftCardSuccessPage,
    isBasketPage,
    isBodyLocked,
    getValidator,
    str2Phone,
    isOver18,
    isValidDate,
    phoneValidator,
    ucbAddressValidator,
    groupIdNumberValidator,
    taxNumberValidator,
    postCodeValidator,
    taxNumberInputLength,
    hypenateTax,
    updateCartLoading,
    basketOperationLoading,
    taxIdValidator,
    taxIdValidate,
    tajValidator,
    tajValidate,
  };
});

function getRealtimeTimeZoneOffsetInHours () {
  return new Date().getTimezoneOffset() / 60;
}
